import { CSSProperties } from 'react';
import { SvgSpriteIcon } from './SvgSpriteIcon';
import {
  CheckboxIconName,
  IconName,
  IconProps,
  IconSize,
  SvgSpriteName,
} from './types';
import { ICON_CLASS_NAME } from './const';
import { Spinner } from './Spinner';
import { getIsCssColorValue } from '@ardoq/color-helpers';
import { SvgSpriteIcon as SvgColorIcon } from './SvgColorIcon';
import { ImageIcon } from './ImageIcon';
import { pick } from 'lodash';
import { colors } from '@ardoq/design-tokens';
import { isShapeSpriteName, isSvgIconSpriteName } from './utils';

const iconClasses = (...classNames: any[]) =>
  [ICON_CLASS_NAME, ...classNames].filter(Boolean).join(' ');

/**
 * @deprecated Use FontAwesomeIcon instead
 */
const LegacyFontAwesomeIcon = ({
  iconName,
  className = '',
  ...rest
}: IconProps) => <b className={iconClasses(iconName, className)} {...rest} />;

/**
 * Generic Icon that supports
 * - Font Awesome
 * - SVGSprite (in house Ardoq's icons)
 * - Color representation icon (square with given color)
 * - Image in icon shape + error image loading handler
 */
const Icon = ({
  iconName,
  src,
  color,
  className,
  iconSize = IconSize.MEDIUM,
  style = {},
  deprecated_onClick,
  ...rest
}: IconProps) => {
  const forwardedProps = {
    ...rest,
    ...(deprecated_onClick ? { onClick: deprecated_onClick } : {}),
  };
  const isImage = typeof src === 'string';
  const isString = typeof iconName === 'string';
  const isShape = isShapeSpriteName(iconName);
  const shapeColor = isShape
    ? style.fill || style.color || colors.black950
    : null;
  const isColor =
    (!iconName && color && getIsCssColorValue(color)) ||
    (isString && getIsCssColorValue(iconName as string)); // for ColorIconName
  const isFontAwesomeIcon = isString && (iconName as string).startsWith('fa ');
  const isSvgSpriteIcon = isSvgIconSpriteName(iconName);
  const isSpinner = iconName === IconName.SPINNER;

  const isCheckboxIcon =
    iconName === CheckboxIconName.CHECKED ||
    iconName === CheckboxIconName.UNCHECKED ||
    iconName === CheckboxIconName.PARTIALLY_CHECKED ||
    iconName === CheckboxIconName.UNSET;

  const iconSizeStyle: CSSProperties = {
    // center FA icons in container
    textAlign: 'center',
    fontSize: isFontAwesomeIcon ? iconSize : undefined,
    width: iconSize,
    height: iconSize,
    minWidth: iconSize,
  };
  const shapeColorStyle = shapeColor ? { color: shapeColor } : undefined;
  const faColorStyle = isFontAwesomeIcon && color ? { color } : undefined;
  const iconStyle = {
    ...iconSizeStyle,
    ...style,
    ...shapeColorStyle,
    ...faColorStyle,
  };

  if (isImage)
    return (
      <ImageIcon
        src={src}
        className={iconClasses(className)}
        iconSize={iconSize}
        aria-hidden
        {...pick(rest, 'data', 'alt')}
      />
    );

  if (isSpinner)
    return (
      <Spinner
        style={iconStyle}
        className={iconClasses(className)}
        {...forwardedProps}
      />
    );

  if (isColor) {
    return (
      <SvgColorIcon
        style={{ ...iconStyle, color: color || iconName }}
        className={iconClasses(className)}
        aria-hidden
        {...forwardedProps}
      />
    );
  }

  if (isSvgSpriteIcon || isShape) {
    return (
      <SvgSpriteIcon
        symbolName={iconName as SvgSpriteName}
        style={iconStyle}
        className={iconClasses(className)}
        color={color}
        aria-hidden={!isCheckboxIcon}
        {...forwardedProps}
      />
    );
  }
  if (isFontAwesomeIcon) {
    return (
      <LegacyFontAwesomeIcon
        iconName={iconName}
        className={className}
        style={iconStyle}
        aria-hidden
        {...forwardedProps}
      />
    );
  }

  return (
    <span
      className={iconClasses(className)}
      style={{ display: 'inline-block', ...iconStyle }}
      aria-hidden
      {...forwardedProps}
    />
  );
};

export { Icon };
